/**
 * Jain Gaurav Yatra — Refund requests (list + approve/reject).
 * Backend: /jain_gaurav_yatra/refund-requests/  (cookie-session auth + X-Tenant-ID).
 *
 * The decision call sends { id, status, admin_note } to the collection URL; if the
 * backend turns out to be a detail-route ViewSet instead, we retry /{id}/ once.
 * Row mapping is tolerant of field aliases, same as payments-api.
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function authHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const nested = (d?.error as Record<string, unknown> | undefined) ?? undefined;
  const msg = pick(d?.message) ?? pick(d?.detail) ?? pick(nested?.message);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const REFUNDS_PATH = "/jain_gaurav_yatra/refund-requests/";

export type RefundStatus = "pending" | "approved" | "rejected" | "processed";

export const REFUND_STATUSES: { value: RefundStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "processed", label: "Processed" },
];

export interface RefundRequest {
  id: string;
  booking_id: string;
  payment_id: string;
  order_id: string;
  requester_name: string;
  phone: string;
  email: string;
  /** Refund amount requested (if the API sends a separate field). */
  amount: number;
  /** Original booking payment amount from the API (`booking_amount`). */
  booking_amount: number;
  reason: string;
  status: RefundStatus;
  admin_note: string;
  created_at: string;
  visit_date: string;
  time_slot: string;
  visitor_count: number;
  member_names: string[];
  bank_name: string;
  ifsc_code: string;
  account_number: string;
  account_holder_name: string;
  /** Linked visit-booking status when the API includes it. */
  booking_status: string;
  /** Set after POST `/visit-bookings/{id}/cancel/` when the API returns them. */
  refund_message: string;
  refund_method: string;
  reviewed_at: string;
  /** Untouched server row, for anything the mapper doesn't surface yet. */
  raw: Record<string, unknown>;
}

function num(v: unknown): number {
  if (v === null || v === undefined || v === "") {return 0;}
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pickStr(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v !== null && v !== undefined && String(v) !== "") {return String(v);}
  }
  return "";
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

/** Resolve booking pk whether the API sends a flat id or nested `{ booking: { id } }`. */
function bookingIdOf(row: Record<string, unknown>): string {
  for (const k of ["booking_id", "booking_reference", "visit_booking_id", "booking"]) {
    const v = row[k];
    if (v === null || v === undefined || v === "") {continue;}
    if (typeof v === "object") {
      const id = pickStr(obj(v), "id", "pk", "uuid");
      if (id) {return id;}
      continue;
    }
    return String(v);
  }
  return "";
}

function toStatus(v: unknown): RefundStatus {
  const s = String(v ?? "").toLowerCase();
  if (["approved", "accept", "accepted"].includes(s)) {return "approved";}
  if (["rejected", "reject", "declined"].includes(s)) {return "rejected";}
  if (["processed", "refunded", "completed", "done"].includes(s)) {return "processed";}
  return "pending";
}

function memberNamesOf(row: Record<string, unknown>, requesterName: string): string[] {
  const raw = row.member_names ?? row.visitor_names;
  if (Array.isArray(raw)) {return raw.map(String).filter(Boolean);}
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return requesterName ? [requesterName] : [];
}

function toRefund(row: Record<string, unknown>): RefundRequest {
  const requesterName = pickStr(row, "requester_name", "name", "full_name", "user_name", "customer_name");
  const memberNames = memberNamesOf(row, requesterName);
  const booking = obj(row.booking ?? row.visit_booking);
  const bookingStatus =
    pickStr(row, "booking_status", "visit_booking_status") ||
    pickStr(booking, "status", "booking_status");
  return {
    id: pickStr(row, "id", "uuid", "pk"),
    booking_id: bookingIdOf(row),
    payment_id: pickStr(row, "payment_id", "payment", "razorpay_payment_id"),
    order_id: pickStr(row, "order_id", "razorpay_order_id", "receipt"),
    requester_name: requesterName,
    phone: pickStr(row, "phone", "contact", "mobile", "phone_number"),
    email: pickStr(row, "email", "user_email"),
    booking_amount: num(
      row.booking_amount ?? row.amount_paid ?? row.final_amount ?? row.total_amount,
    ),
    amount: num(row.refund_amount ?? row.amount_inr ?? row.amount ?? row.total),
    reason: pickStr(row, "reason", "refund_reason", "description", "remarks"),
    status: toStatus(row.status ?? row.refund_status ?? row.state),
    admin_note: pickStr(row, "admin_note", "admin_notes", "note", "internal_notes"),
    created_at: pickStr(row, "created_at", "created", "requested_at", "timestamp", "date"),
    visit_date: pickStr(row, "visit_date", "booking_date"),
    time_slot: pickStr(row, "time_slot", "booking_time"),
    visitor_count: num(row.visitor_count ?? row.group_size) || memberNames.length || 1,
    member_names: memberNames,
    bank_name: pickStr(row, "bank_name", "bank"),
    ifsc_code: pickStr(row, "ifsc_code", "ifsc"),
    account_number: pickStr(row, "account_number", "account_no"),
    account_holder_name: pickStr(row, "account_holder_name", "account_name", "holder_name"),
    booking_status: bookingStatus,
    refund_message: pickStr(row, "refund_message"),
    refund_method: pickStr(row, "refund_method"),
    reviewed_at: pickStr(row, "reviewed_at", "processed_at", "refunded_at"),
    raw: row,
  };
}

function rowsOf(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {return data as Record<string, unknown>[];}
  const d = data as Record<string, unknown> | undefined;
  for (const key of ["results", "data", "refund_requests", "requests"]) {
    const v = d?.[key];
    if (Array.isArray(v)) {return v as Record<string, unknown>[];}
    if (v && typeof v === "object") {
      const inner = (v as Record<string, unknown>).results;
      if (Array.isArray(inner)) {return inner as Record<string, unknown>[];}
    }
  }
  return [];
}

export async function listRefundRequests(status?: RefundStatus): Promise<RefundRequest[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await fetch(`${base}${REFUNDS_PATH}${qs}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return rowsOf(data).map(toRefund);
}

export interface RefundDecision {
  id: string;
  status: RefundStatus;
  admin_note: string;
}

/** Approve/reject a refund request. */
export async function decideRefundRequest(payload: RefundDecision): Promise<RefundRequest> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const body = JSON.stringify({
    // Numeric ids are sent as numbers — the sample payload uses `"id": 1`.
    id: /^\d+$/.test(payload.id) ? Number(payload.id) : payload.id,
    status: payload.status,
    admin_note: payload.admin_note,
  });

  const send = (url: string) =>
    fetch(url, { method: "PATCH", headers: authHeaders(), credentials: "include", body });

  let res = await send(`${base}${REFUNDS_PATH}`);
  if (res.status === 404 || res.status === 405) {
    res = await send(`${base}${REFUNDS_PATH}${payload.id}/`);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const row = (data as Record<string, unknown>)?.data ?? data;
  return toRefund((row ?? {}) as Record<string, unknown>);
}

const VISIT_BOOKINGS_PATH = "/jain_gaurav_yatra/visit-bookings/";

function refundFromCancelResponse(
  data: Record<string, unknown>,
  request: RefundRequest,
): RefundRequest {
  const refundMessage = pickStr(data, "refund_message", "detail");
  const refundMethod = pickStr(data, "refund_method");
  const refundAmount = num(data.refund_amount);

  const linked = rowsOf({ refund_requests: data.refund_requests });
  const match =
    linked.find((row) => pickStr(row, "id") === request.id) ??
    linked.find((row) => bookingIdOf(row) === request.booking_id) ??
    linked[0];

  if (match) {
    return {
      ...toRefund(match),
      amount: refundAmount || num(match.refund_amount) || request.amount,
      refund_message: refundMessage,
      refund_method: refundMethod,
    };
  }

  const row = obj(data.refund_request ?? data.data);
  if (pickStr(row, "id")) {
    return {
      ...toRefund(row),
      amount: refundAmount || request.amount,
      refund_message: refundMessage,
      refund_method: refundMethod,
    };
  }

  return {
    ...request,
    status: "processed",
    amount: refundAmount || request.amount,
    booking_status: "cancelled",
    refund_message: refundMessage,
    refund_method: refundMethod,
  };
}

/**
 * Step 2 — cancel booking and refund via POST `/visit-bookings/{booking_id}/cancel/`.
 * No request body; backend refunds via Razorpay and marks linked refund-requests processed.
 */
export async function processPaymentRefund(request: RefundRequest): Promise<RefundRequest> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  if (!request.booking_id) {throw new Error("Booking id missing on this refund request.");}
  if (request.status !== "approved") {
    throw new Error("Only approved refund requests can be processed.");
  }

  const res = await fetch(`${base}${VISIT_BOOKINGS_PATH}${request.booking_id}/cancel/`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const msg = errMessage(data, res.status);
    if (/already cancelled/i.test(msg)) {
      return {
        ...request,
        status: "processed",
        booking_status: "cancelled",
        admin_note: request.admin_note || "Booking already cancelled",
      };
    }
    throw new Error(msg);
  }

  return refundFromCancelResponse(data, request);
}
